import { useState, useEffect } from 'react';
import { collection, getDocs, deleteDoc, doc, query, orderBy, limit, startAfter, DocumentSnapshot, getCountFromServer } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { SonoReport } from '../../types/sonoReport';
import { Plus, Eye, Trash2, Radio, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { ReportFilterBar, ReportFilters, defaultFilters, isFiltersActive, applyFilters } from '../ui/ReportFilterBar';
import SonoForm from './SonoForm';
import SonoReportPreview from './SonoReportPreview';
import { format } from '../../utils/format';
import { useConfirm } from '../../contexts/ConfirmContext';
import SonoTrendChart from './SonoTrendChart';
import { canDeleteReport } from '../../utils/permissions';

export default function SonoReportsList() {
  const confirm = useConfirm();
  const { user } = useAuth();
  const [reports, setReports] = useState<SonoReport[]>([]);
  const [allReports, setAllReports] = useState<SonoReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState<ReportFilters>(defaultFilters());
  const [showForm, setShowForm] = useState(false);
  const [selectedReport, setSelectedReport] = useState<SonoReport | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastDoc, setLastDoc] = useState<DocumentSnapshot | null>(null);
  const [firstDoc, setFirstDoc] = useState<DocumentSnapshot | null>(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [totalReportsCount, setTotalReportsCount] = useState(0);
  const [pageStartCursors, setPageStartCursors] = useState<Map<number, DocumentSnapshot>>(new Map());
  const reportsPerPage = 10;

  const loadReports = async (page = 1, direction: 'next' | 'prev' | 'direct' = 'direct', cursor?: DocumentSnapshot | null) => {
    try {
      setLoading(true);
      const reportsRef = collection(db, 'sonoReports');
      
      // Charger le nombre total de rapports (seulement au premier chargement)
      if (page === 1 && direction === 'direct') {
        const countSnapshot = await getCountFromServer(query(reportsRef));
        setTotalReportsCount(countSnapshot.data().count);
        
        // Charger tous les rapports pour les statistiques et la recherche
        const allReportsQuery = query(reportsRef, orderBy('date', 'desc'));
        const allSnapshot = await getDocs(allReportsQuery);
        const allReportsData = allSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as SonoReport[];
        setAllReports(allReportsData);
      }
      
      let q;
      
      if (direction === 'next' && cursor) {
        q = query(reportsRef, orderBy('date', 'desc'), startAfter(cursor), limit(reportsPerPage + 1));
      } else if (direction === 'prev' && cursor) {
        q = query(reportsRef, orderBy('date', 'asc'), startAfter(cursor), limit(reportsPerPage + 1));
      } else if (direction === 'direct' && page > 1 && pageStartCursors.has(page)) {
        const startCursor = pageStartCursors.get(page)!;
        q = query(reportsRef, orderBy('date', 'desc'), startAfter(startCursor), limit(reportsPerPage + 1));
      } else {
        q = query(reportsRef, orderBy('date', 'desc'), limit(reportsPerPage + 1));
      }
      
      const snapshot = await getDocs(q);
      let reportsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as SonoReport[];
      
      if (direction === 'prev') {
        reportsData = reportsData.reverse();
      }
      
      const hasMore = reportsData.length > reportsPerPage;
      if (hasMore) {
        reportsData.pop();
      }
      
      setReports(reportsData);
      setHasNextPage(hasMore);
      setHasPrevPage(page > 1);
      setCurrentPage(page);
      
      if (reportsData.length > 0) {
        const newFirstDoc = snapshot.docs[0];
        const newLastDoc = snapshot.docs[reportsData.length - 1];
        
        setFirstDoc(newFirstDoc);
        setLastDoc(newLastDoc);
        
        if (!pageStartCursors.has(page)) {
          setPageStartCursors(prev => new Map(prev).set(page, newFirstDoc));
        }
      }
    } catch (err) {
      console.error('Erreur lors du chargement des rapports:', err);
      setError('Erreur lors du chargement des rapports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
  }, []);

  const getTotalPages = () => Math.ceil(totalReportsCount / reportsPerPage);

  const goToNextPage = () => {
    if (hasNextPage && lastDoc) {
      loadReports(currentPage + 1, 'next', lastDoc);
    }
  };

  const goToPrevPage = () => {
    if (hasPrevPage && firstDoc) {
      loadReports(currentPage - 1, 'prev', firstDoc);
    }
  };

  const goToPage = (pageNumber: number) => {
    if (pageNumber === 1) {
      setPageStartCursors(new Map());
      loadReports(1, 'direct');
    } else if (pageStartCursors.has(pageNumber)) {
      loadReports(pageNumber, 'direct');
    } else {
      goToPage(1);
    }
  };

  const handleDelete = async (id: string) => {
    const ok = await confirm({ title: 'Supprimer le rapport', description: 'Êtes-vous sûr de vouloir supprimer ce rapport Sono ?', confirmLabel: 'Supprimer', cancelLabel: 'Annuler', variant: 'danger' });
    if (!ok) return;
    
    try {
      await deleteDoc(doc(db, 'sonoReports', id));
      loadReports(currentPage, 'direct', currentPage > 1 ? pageStartCursors.get(currentPage) : null);
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      setError('Erreur lors de la suppression du rapport');
    }
  };

  const handleFormSubmit = () => {
    setShowForm(false);
    setCurrentPage(1);
    setPageStartCursors(new Map());
    loadReports(1, 'direct');
  };

  const handlePreview = (report: SonoReport) => {
    setSelectedReport(report);
    setShowPreview(true);
  };

  const filtersActive = isFiltersActive(filters);
  const filteredReports = filtersActive
    ? applyFilters(allReports, filters, r => r.date, r => [new Date(r.date).toLocaleDateString('fr-FR'), r.generalObservations ?? ''])
    : reports;

  const statsReports = allReports.length > 0 ? allReports : reports;
  const totalPages = getTotalPages();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#00665C]"></div>
      </div>
    );
  }


  return (

    <>
      {/* ===== MOBILE VIEW (< md) ===== */}
      <div className="md:hidden">
        <div className="min-h-screen bg-gray-50">
      {showForm && (
        <SonoForm onSuccess={handleFormSubmit} onClose={() => setShowForm(false)} />
      )}
      {showPreview && selectedReport && (
        <SonoReportPreview report={selectedReport} onClose={() => { setShowPreview(false); setSelectedReport(null); }} />
      )}
      {!showForm && !showPreview && (
        <>
          <div className="bg-vh-green text-white px-4 pt-10 pb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Radio className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold tracking-tight">Sono</h1>
                  <p className="text-white/70 text-sm">Gestion des rapports</p>
                </div>
              </div>
              <button onClick={() => setShowForm(true)} className="w-12 h-12 bg-vh-gold rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform">
                <Plus className="w-6 h-6 text-white" />
              </button>
            </div>
            <div className="bg-white/10 rounded-xl p-3 flex items-center justify-center gap-2">
              <span className="text-2xl font-bold">{filteredReports.length}</span>
              <span className="text-white/70 text-sm">rapport(s) affichés</span>
            </div>
          </div>
          <div className="sticky top-0 z-10 bg-white shadow-sm px-4 py-2">
            <ReportFilterBar filters={filters} onChange={setFilters} />
          </div>
          {loading && <div className="flex justify-center items-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-vh-green" /></div>}
          {error && <div className="mx-4 mt-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>}
          {!loading && (
            <div className="px-4 py-4 space-y-3">
              {filteredReports.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                  <Radio className="w-12 h-12 mb-3 text-gray-300" />
                  <p className="text-base font-medium">Aucun rapport trouvé</p>
                  <p className="text-sm mt-1">Appuyez sur + pour créer un rapport</p>
                </div>
              ) : (
                filteredReports.map(report => (
                  <div key={report.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="flex items-stretch">
                      <div className="w-14 bg-vh-green flex flex-col items-center justify-center py-3 flex-shrink-0">
                        <span className="text-white font-bold text-xl leading-none">
                          {report.date?.toDate ? report.date.toDate().getDate() : new Date(report.date ?? 0).getDate()}
                        </span>
                        <span className="text-white/80 text-xs mt-0.5">
                          {report.date?.toDate ? report.date.toDate().toLocaleDateString('fr-FR', { month: 'short' }) : new Date(report.date ?? 0).toLocaleDateString('fr-FR', { month: 'short' })}
                        </span>
                      </div>
                      <div className="flex-1 px-3 py-2.5 min-w-0">
                        <p className="font-semibold text-gray-800 text-sm">Rapport Sono</p>
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                          {report.beforeService && (
                            <span className="text-xs bg-vh-green/10 text-vh-green px-2 py-0.5 rounded-full font-medium">
                              {Object.values(report.beforeService).filter(Boolean).length} checks avant
                            </span>
                          )}
                          {report.duringService && (
                            <span className="text-xs bg-vh-gold/10 text-vh-gold px-2 py-0.5 rounded-full font-medium">
                              {Object.values(report.duringService).filter(Boolean).length} checks pendant
                            </span>
                          )}
                          {report.afterService && (
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">
                              {Object.values(report.afterService).filter(Boolean).length} checks après
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col border-l border-gray-100 flex-shrink-0">
                        <button onClick={() => handlePreview(report)} className="flex-1 flex items-center justify-center px-3 text-blue-500 hover:bg-blue-50 transition-colors">
                          <Eye className="w-4 h-4" />
                        </button>
                        {canDeleteReport(report, user) && (
                          <>
                            <div className="border-t border-gray-100" />
                            <button onClick={() => handleDelete(report.id)} className="flex-1 flex items-center justify-center px-3 text-red-400 hover:bg-red-50 transition-colors">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
          {(hasPrevPage || hasNextPage) && (
            <div className="flex items-center justify-between px-4 pb-6 pt-2">
              <button onClick={goToPrevPage} disabled={!hasPrevPage} className={hasPrevPage ? 'flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-vh-green text-white' : 'flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-gray-100 text-gray-400 cursor-not-allowed'}>
                <ChevronLeft className="w-4 h-4" /> Précédent
              </button>
              <span className="text-sm text-gray-500 font-medium">Page {currentPage}</span>
              <button onClick={goToNextPage} disabled={!hasNextPage} className={hasNextPage ? 'flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-vh-green text-white' : 'flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-gray-100 text-gray-400 cursor-not-allowed'}>
                Suivant <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
      </div>

      {/* ===== DESKTOP VIEW (>= md) ===== */}
      <div className="hidden md:block">
        <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Radio className="h-8 w-8 text-[#00665C]" />
              <h1 className="text-3xl font-bold text-gray-900">Communication & Sono</h1>
            </div>
            <p className="text-gray-600">
              Gérez les rapports du département Communication & Sono
            </p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
              showForm 
                ? 'bg-gray-600 text-white hover:bg-gray-700' 
                : 'bg-[#00665C] text-white hover:bg-[#004d45]'
            }`}
          >
            {showForm ? (
              <>
                <X className="h-4 w-4" />
                Fermer le formulaire
              </>
            ) : (
              <>
                <Plus className="h-4 w-4" />
                Nouveau rapport
              </>
            )}
          </button>
        </div>

        {/* Statistiques rapides */}
        {statsReports.length > 0 && (
          <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg shadow-sm" style={{ borderLeft: '4px solid #00665C' }}>
              <h3 className="text-sm font-medium text-gray-500">Total Rapports</h3>
              <p className="text-2xl font-bold text-[#00665C]">{totalReportsCount}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm" style={{ borderLeft: '4px solid #10B981' }}>
              <h3 className="text-sm font-medium text-gray-500">Matériel OK</h3>
              <p className="text-2xl font-bold text-green-600">
                {statsReports.filter(r => r.beforeService.materialCheck === 'OK').length}
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm" style={{ borderLeft: '4px solid #3B82F6' }}>
              <h3 className="text-sm font-medium text-gray-500">Lives Effectués</h3>
              <p className="text-2xl font-bold text-blue-600">
                {statsReports.filter(r => r.duringService.liveStreaming === 'OUI').length}
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm" style={{ borderLeft: '4px solid #8B5CF6' }}>
              <h3 className="text-sm font-medium text-gray-500">Dernier Rapport</h3>
              <p className="text-lg font-semibold text-gray-900">
                {format.date(statsReports[0]?.date || '')}
              </p>
            </div>
          </div>
        )}

        {/* Actions et recherche */}
        <div className="mb-6">
          <ReportFilterBar
            filters={filters}
            onChange={setFilters}
            onReset={() => setFilters(defaultFilters())}
            placeholder="Rechercher par date ou observations…"
            resultCount={filteredReports.length}
            totalCount={allReports.length || totalReportsCount}
          />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Formulaire inline pour nouveau rapport */}
        {showForm && (
          <div className="mb-6">
            <SonoForm
              onSuccess={handleFormSubmit}
              onClose={() => setShowForm(false)}
            />
          </div>
        )}


      {/* Graphique Sono */}
      <SonoTrendChart reports={allReports} />
        {/* Liste des rapports */}
        {filteredReports.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <Radio className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun rapport trouvé</h3>
            <p className="text-gray-600 mb-6">
              {filters.search ? 'Aucun rapport ne correspond à votre recherche.' : 'Commencez par créer votre premier rapport.'}
            </p>
            {!filters.search && (
              <button
                onClick={() => setShowForm(true)}
                className="bg-[#00665C] text-white px-4 py-2 rounded-lg hover:bg-[#004d45] transition-colors inline-flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Créer un rapport
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Version mobile */}
            <div className="sm:hidden">
              {filteredReports.map((report) => (
                <div key={report.id} className="border-b border-gray-100 p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-medium text-gray-900">
                      {format.date(report.date)}
                    </h3>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handlePreview(report)}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      {canDeleteReport(report, user) && (
                      <button
                        onClick={() => handleDelete(report.id!)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      )}
                    </div>
                  </div>
                  <div className="text-sm text-gray-600">
                    <div className="grid grid-cols-2 gap-2">
                      <div>Matériel: {report.beforeService.materialCheck}</div>
                      <div>Live: {report.duringService.liveStreaming}</div>
                      <div>Audio Replay: {report.afterService.audioReplayPublication}</div>
                      <div>Vidéo Replay: {report.afterService.videoReplayPublication}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Version desktop */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Matériel Sono
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Live Streaming
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Audio Replay
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Vidéo Replay
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {filteredReports.map((report) => (
                    <tr key={report.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {format.date(report.date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          report.beforeService.materialCheck === 'OK' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {report.beforeService.materialCheck}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          report.duringService.liveStreaming === 'OUI' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {report.duringService.liveStreaming}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          report.afterService.audioReplayPublication === 'OK' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {report.afterService.audioReplayPublication}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          report.afterService.videoReplayPublication === 'OK' 
                            ? 'bg-green-100 text-green-800' 
                            : report.afterService.videoReplayPublication === 'EN_COURS'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {report.afterService.videoReplayPublication === 'EN_COURS' ? 'En cours' : report.afterService.videoReplayPublication}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handlePreview(report)}
                            className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded"
                            title="Prévisualiser"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          {canDeleteReport(report, user) && (
                          <button
                            onClick={() => handleDelete(report.id!)}
                            className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded"
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      
      {/* Pagination */}
      {!filtersActive && filteredReports.length > 0 && totalPages > 1 && (
        <div className="bg-white rounded-lg shadow-sm p-4 mt-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-600">
              Page {currentPage} sur {totalPages} • {totalReportsCount} rapports au total
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={goToPrevPage}
                disabled={!hasPrevPage}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  hasPrevPage
                    ? 'text-[#00665C] bg-[#00665C]/8 hover:bg-[#00665C]/15'
                    : 'text-gray-400 bg-gray-100 cursor-not-allowed'
                }`}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Précédent
              </button>
              
              <div className="flex items-center space-x-1">
                {(() => {
                  const pageNumbers: (number | string)[] = [];
                  
                  // Toujours inclure la page 1
                  if (totalPages > 0) {
                    pageNumbers.push(1);
                  }
                  
                  // Ajouter des ellipses si nécessaire avant la page actuelle
                  if (currentPage > 4) {
                    pageNumbers.push('...');
                  }
                  
                  // Ajouter les pages autour de la page actuelle
                  const startPage = Math.max(2, currentPage - 1);
                  const endPage = Math.min(totalPages - 1, currentPage + 1);
                  
                  for (let i = startPage; i <= endPage; i++) {
                    if (!pageNumbers.includes(i)) {
                      pageNumbers.push(i);
                    }
                  }
                  
                  // Ajouter des ellipses si nécessaire après la page actuelle
                  if (currentPage < totalPages - 3) {
                    pageNumbers.push('...');
                  }
                  
                  // Toujours inclure la dernière page si elle n'est pas déjà incluse
                  if (totalPages > 1 && !pageNumbers.includes(totalPages)) {
                    pageNumbers.push(totalPages);
                  }
                  
                  return pageNumbers.map((pageNum, index) => {
                    if (pageNum === '...') {
                      return (
                        <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                          ...
                        </span>
                      );
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => goToPage(pageNum as number)}
                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                          pageNum === currentPage
                            ? 'bg-[#00665C] text-white'
                            : 'text-[#00665C] bg-[#00665C]/8 hover:bg-[#00665C]/15'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  });
                })()}
              </div>
              
              <button
                onClick={goToNextPage}
                disabled={!hasNextPage}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  hasNextPage
                    ? 'text-[#00665C] bg-[#00665C]/8 hover:bg-[#00665C]/15'
                    : 'text-gray-400 bg-gray-100 cursor-not-allowed'
                }`}
              >
                Suivant
                <ChevronRight className="h-4 w-4 ml-1" />
              </button>
            </div>
          </div>
        </div>
      )}

      {showPreview && selectedReport && (
        <SonoReportPreview
          report={selectedReport}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
      </div>
    </>
  );
}
